import { useQueryClient } from '@tanstack/react-query';

import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import { executeDiscoverySearch, type ExecuteDiscoverySearchRequest } from '../api';
import { useAbortableMutation } from '@/shared/hooks/use-abortable-mutation';

import { useDiscoveryStore } from '@/features/discovery/stores/discovery-store';
import { searchCreatorPendingAtom } from '@/features/discovery/stores/mutation-atom';

type ExecuteDiscoverySearchMutationRequest = {
  discoverySessionId: string;
  request: ExecuteDiscoverySearchRequest;
};

export function useExecuteDiscoverySearch() {
  const { setSession } = useDiscoveryStore();
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useAbortableMutation({
    mutationFn: ({ discoverySessionId, request }: ExecuteDiscoverySearchMutationRequest, signal) =>
      executeDiscoverySearch(discoverySessionId, request, signal),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.discovery.searches() });
    },
    pendingAtom: searchCreatorPendingAtom,
    requestKey: 'discovery:execute-search',
    onSuccess: (response, variables) => {
      setSession((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          current_search_history_id: response.search_id,
        };
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.discovery.currentSession() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.searchStatus(
          variables.discoverySessionId,
          response.search_id,
        ),
      });
    },
  });
}
